<template>
  <section
    :class="[
      'grid grid-cols-[32px_1fr] gap-x-2 @container items-end',
      { 'backlight-animation': light, 'block!': !room?.isGroup }
    ]"
    :data-timestamp="event.timestamp"
    aria-label="Incoming chat message"
    data-test="chat-message-incoming"
  >
    <contact-avatar
      v-if="room?.isGroup && isLastInSeries"
      data-test="avatar"
      class="cursor-pointer self-end"
      :contact="contact"
      :contact-type="contact?.type"
      :name="avatarName"
      :size="32"
      @click="emit('clickEventAvatar', event)"
    />
    <section
      :class="['grid gap-y-1 chat-message-max-w w-fit min-w-0 items-start', { 'col-start-2': !isLastInSeries }]"
      data-test="chat-event-incoming-section"
      @mouseenter="onMouseEnter"
      @mouseleave="isHovered = false"
    >
      <section
        v-if="hasForwarding && shouldRenderHeavy && !isMessageDeleted"
        class="relative grid w-full rounded-12 bg-comp-messages-bubble-bg-inc-def"
        data-test="chat-message-incoming-forward-bubble"
      >
        <header
          v-if="!isGroup && room?.isGroup"
          ref="header"
          class="mb-1 flex items-center justify-between gap-1 overflow-hidden px-3 pt-2 text-body-m-14-20"
          :class="`text-${color}`"
          :style="{
            width: headerWidth && isHovered ? `${headerWidth}px` : 'auto'
          }"
          data-test="message-header"
        >
          <span class="truncate shrink">
            {{ authorName }}
          </span>
          <a
            v-if="isHovered"
            data-test="header-reply"
            class="hover:underline cursor-pointer font-light"
            @click.prevent="emit('reply')"
          >
            {{ $t('common.toAnswer') }}
          </a>
        </header>
        <forward-message
          :id="event.eventId"
          class="mb-2"
          :forwarding="event.item.forwarding!"
          :event="event"
          :can-expand-text="true"
          @dblclick.stop
        />
        <div
          v-if="!hasOwnContent"
          :class="voiceFooterClass"
        >
          <message-reactions
            v-if="shouldRenderHeavy && eventReactions"
            :event-reactions="eventReactions"
            :incoming="true"
            :is-show-reaction-info="room?.isGroup ?? false"
            class="w-full min-w-0"
            @toggle-reaction="toggleReaction"
            @dblclick.stop
          />
          <div class="flex items-center justify-end">
            <div class="flex items-center gap-x-1">
              <p
                v-if="isEdited"
                class="text-label-r-12-18 text-fg-base-fg22"
                :title="$t('chat.editedTimes') + ': ' + event.item.era"
              >
                {{ $t('chat.edited') }}
              </p>
              <wui-icon
                v-if="isPinned"
                name="pinM"
                class="h-4! w-4! text-fg-base-fg22"
                :title="$t('chat.messageIsPinned')"
              />
              <p
                class="text-label-r-12-18 text-fg-base-fg22"
                data-test="incoming-message-time"
              >
                {{ hhmm }}
              </p>
            </div>
          </div>
        </div>
        <span
          v-if="isUnread && !hasOwnContent"
          class="absolute right-[-10px] -bottom-px h-2 w-2 rounded-full bg-primary-tints-400"
        />
      </section>

      <section
        v-if="hasOwnContent"
        class="relative grid w-full rounded-12 bg-comp-messages-bubble-bg-inc-def"
        data-test="chat-message-incoming-content-bubble"
      >
        <header
          v-if="!hasForwarding && !isGroup && room?.isGroup"
          ref="header"
          class="mb-1 flex items-center justify-between gap-1 overflow-hidden px-2 pt-2 text-body-m-14-20"
          :class="`text-${color}`"
          :style="{
            width: headerWidth && isHovered ? `${headerWidth}px` : 'auto'
          }"
          data-test="message-header"
        >
          <span class="truncate shrink">
            {{ authorName }}
          </span>
          <a
            v-if="isHovered"
            data-test="header-reply"
            class="hover:underline cursor-pointer font-light"
            @click.prevent="emit('reply')"
          >
            {{ $t('common.toAnswer') }}
          </a>
        </header>
        <template
          v-if="
            shouldRenderHeavy &&
              !isMessageDeleted &&
              (quotedEvent?.item.messageText || !!quotedEvent?.item.files?.length ||
                (quotedEvent?.item.forwarding && quotedEvent?.item.forwarding.forwardingEventId))"
        >
          <quoted-message
            :id="event.eventId"
            :class="quoteBlockClass"
            :event="quotedEvent"
            :selected-text="event.item.quote?.quotedText!"
            :is-clickable="true"
            :can-expand-text="true"
            @dblclick.stop
            @click="scrollToQuotedEvent"
          />
        </template>
        <div
          v-else-if="!shouldRenderHeavy && !isMessageDeleted && event.item.quote?.quotedEventId"
          :class="quoteBlockClass"
          class="w-full"
          aria-hidden="true"
        />
        <vcard-in-message
          v-if="shouldRenderHeavy && isSingleVCardFile && event.item.files?.[0]"
          class="m-2"
          :file="event.item.files?.[0]"
        >
          <template #add-to-chat="{ pServed }">
            <add-contact-to-chats-button
              v-if="pServed"
              :p-served="pServed"
            >
              <template #default="{ open }">
                <button
                  type="button"
                  class="min-w-0 flex-1 text-center text-label-m-13-18 text-comp-link-neut-def"
                  data-test="vcard-add-to-chat-button"
                  @click.stop="open"
                >
                  {{ $t('chat.addToChat') }}
                </button>
              </template>
            </add-contact-to-chats-button>
          </template>
        </vcard-in-message>
        <chat-event-list-item-file-list
          v-else-if="shouldRenderHeavy && event.item?.files?.length"
          :files="event.item.files"
          :type="'IncomingMessage'"
          :event="event"
          :is-pinned="isPinned"
          :class="isVoice ? '' : 'mb-1'"
          @dblclick.stop
          @images-loaded="emit('resize')"
        />
        <div
          v-else-if="!shouldRenderHeavy && isSingleVCardFile"
          class="m-2 w-full min-h-[160px]"
          aria-hidden="true"
        />
        <div
          v-else-if="!shouldRenderHeavy && event.item?.files?.length"
          class="w-full"
          :style="{ minHeight: '120px' }"
          aria-hidden="true"
        />
        <i
          v-if="isMessageDeleted"
          class="text-1316 px-2 py-2"
          @dblclick.stop
        >{{ $t('chat.messageRemoved') }}</i>
        <p
          v-else-if="messageText"
          ref="textElement"
          data-test="chat-event-incoming-message-container"
          class="
            select-text wrap-break-word whitespace-pre-wrap px-2 pb-1 text-body-r-15-22 text-fg-base-fg3
            [&_a]:text-comp-link-neut-def
            [&_b[data-contact-uri]]:cursor-pointer [&_b[data-contact-uri]]:font-normal [&_b[data-contact-uri]]:text-surf-brand-basedarkblue-def
          "
          :class="[
            messageClasses,
            (!isGroup && room?.isGroup) ? 'pt-1' : 'pt-2',
          ]"
          @click="onMessageHtmlClick"
          @dblclick.stop
          v-html="messageText"
        />
        <div
          v-if="isTextTruncated"
          class="mx-2 mt-1 flex cursor-pointer items-center pr-1"
          @click="toggleExpand"
        >
          <p class="text-body-r-14-20 text-comp-link-neut-def hover:underline">
            {{ isExpanded ? $t('common.collapse') : $t('common.expand') }}
          </p>
          <wui-icon
            :name="isExpanded ? 'chevronUpSmM' : 'chevronDownSmM'"
            class="text-comp-link-neut-def h-5! w-5!"
          />
        </div>

        <div
          :class="voiceFooterClass"
        >
          <message-reactions
            v-if="shouldRenderHeavy && eventReactions"
            :event-reactions="eventReactions"
            :incoming="true"
            :is-show-reaction-info="room?.isGroup ?? false"
            class="w-full min-w-0"
            @toggle-reaction="toggleReaction"
            @dblclick.stop
          />
          <div class="flex items-center justify-end">
            <div class="flex items-center gap-x-1">
              <p
                v-if="isEdited && !isMessageDeleted"
                class="text-label-r-12-18 text-fg-base-fg22"
                :title="$t('chat.editedTimes') + ': ' + event.item.era"
              >
                {{ $t('chat.edited') }}
              </p>
              <wui-icon
                v-if="isPinned"
                name="pinM"
                class="h-4! w-4! text-fg-base-fg22"
                :title="$t('chat.messageIsPinned')"
              />
              <p
                class="text-label-r-12-18 text-fg-base-fg22"
                data-test="incoming-message-time"
              >
                {{ hhmm }}
              </p>
            </div>
          </div>
        </div>
        <span
          v-if="isUnread"
          class="absolute right-[-10px] -bottom-px h-2 w-2 rounded-full bg-primary-tints-400"
        />
      </section>
    </section>
  </section>
</template>

<script lang="ts" setup>
import { WuiIcon as WuiIcon, useRenderNotifier } from '@wui/common-library'
import { BOT_CONTROL_P_SERVED, HistoryMessageItem, MessageIncomingEvent, MessageOutgoingEvent, PinnedMessage, ReactionMessagePayload } from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed, toRefs, ref, onMounted } from 'vue'

import { AddContactToChatsButton } from '@/features/add-contact-to-chats'
import { MessageReactions } from '@/features/message-reaction'

import { ChatEventListItemFileList } from '@/entities/chat-event-file'
import { isSameMessageSeries, useChatEvent } from '@/entities/chat-handlers'
import { useStoreContacts } from '@/entities/contact'
import { ContactAvatar } from '@/entities/contact-avatar'
import { usePinEventsStore, ForwardMessage, QuotedMessage, useUnreadMessages, VcardInMessage } from '@/entities/messages'
import { useReactionsStore } from '@/entities/reaction'
import { useStoreRooms } from '@/entities/room'
import { useRoom } from '@/entities/use-room'
import { isVoiceMessage, useVoiceMessageStore } from '@/entities/voice-message'

import { useLocalization } from '@/shared/composables/localization'
import { LINES_COUNT_TO_CLAMP_MESSAGE, useTextExpand } from '@/shared/composables/use-text-expand'
import { useUserColor } from '@/shared/composables/user-color'
import { EmittedReaction } from '@/shared/types/emitted-reaction'
import { getUnknownContactLabel, pickBetterNamedContact } from '@/shared/utils/contacts'
import { createBotControlLinksFromText, getBotControlLinkFromEvent } from '@/shared/utils/create-links-from-text'
import { formatMessageText } from '@/shared/utils/text-entities'
import { isVCardFile } from '@/shared/utils/vcard'

const props = defineProps<{
  event: HistoryMessageItem<MessageIncomingEvent>
  next?: HistoryMessageItem<MessageIncomingEvent>
  following?: HistoryMessageItem<MessageIncomingEvent>
  quotedEvent?: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> | undefined
  small?: boolean
  light?: boolean
  isEdited?: boolean
  isMessageDeleted?: boolean
  /** When false — skip files/quotes/reactions until near viewport (faster first measure). */
  renderHeavy?: boolean
}>()

/** Default true for callers outside the virtualized chat list. */
const shouldRenderHeavy = computed(() => props.renderHeavy !== false)

const emit = defineEmits<{
  (event: 'clickEventAvatar', value: HistoryMessageItem<MessageIncomingEvent>): void
  (event: 'scrollToItem', targetEventId: number, sourceEventId?: number): void
  (event: 'reply'): void
  (event: 'resize'): void
}>()

onMounted(() => {
  initTextExpanding()
})

const { event, next, following, isMessageDeleted } = toRefs(props)
const reactionsStore = useReactionsStore()

const { reactionEvents } = storeToRefs(reactionsStore)
const { transcripts, expandedTranscripts } = storeToRefs(useVoiceMessageStore())
const { makeOutgoingReaction, removeOutgoingReaction } = reactionsStore
const { findByPServed: findRoom } = useStoreRooms()
const { getPinEventsForRoom } = usePinEventsStore()
const { findGuestById, findByPServed } = useStoreContacts()
const { t } = useLocalization()
const { getTextColorByName } = useUserColor()
const { notifySuccess, notifyError } = useRenderNotifier()

const isHovered = ref(false)
const header = ref<HTMLElement | undefined>()
const headerWidth = ref<number | undefined>()

const room = computed(() => findRoom(props.event.item.pServed))
const eventReactions = computed(() => reactionEvents.value.get(event.value.eventId))
const pinEvents = computed(() => getPinEventsForRoom(props.event.item.pServed))

const toggleReaction = (emittedReaction: EmittedReaction) => {
  if (!room.value) return

  const payload: ReactionMessagePayload = {
    to: room.value.pServedUser,
    targetEventId: event.value.eventId,
    reactionType: emittedReaction.emoji,
  }

  if (emittedReaction.isSetByCurrentUser) {
    removeOutgoingReaction(payload)
  } else {
    makeOutgoingReaction(payload)
  }
}

const contact = computed(() => {
  const executor = event.value.item.executor
  return pickBetterNamedContact(
    executor,
    findByPServed(executor),
    room.value?.subscribers.get(executor),
    [...(room.value?.subscribers.values() ?? [])].find(item => item.imLogin === executor),
  )
})

const guest = computed(() => {
  if (!props.event.item.executor.includes('<sip:GUEST')) {
    return undefined
  }
  return room.value?.guests?.get(props.event.item.executor) || findGuestById(props.event.item.executor)
})

const authorName = computed(() => {
  if (contact.value?.name) {
    return contact.value.name
  }
  if (guest.value?.name) {
    return `${guest.value.name} (${t('common.guest')})`
  }
  return getUnknownContactLabel(props.event.item.executor)
})

const color = computed(() => {
  if (!authorName.value) {
    return 'comp-avatar-user-blue-mid'
  }
  return getTextColorByName(authorName.value, false)
})

const { hhmm, isGroup, messageClasses } = useChatEvent(event, next)
const isLastInSeries = computed(() => !isSameMessageSeries(event.value, following.value))
const scrollToQuotedEvent = () => {
  if (props.quotedEvent?.eventId && props.event.eventId) {
    emit('scrollToItem', props.quotedEvent.eventId, props.event.eventId)
  }
}

const avatarName = computed(() => {
  if (contact.value?.name) {
    return contact.value.name
  }

  return guest.value?.name
})

const isBotControlChat = computed(() => event.value.item.pServed === BOT_CONTROL_P_SERVED)

const messageText = computed(() => {
  const rawText = event.value.item.messageText
  return isBotControlChat.value
    ? createBotControlLinksFromText(rawText)
    : formatMessageText(rawText, event.value.item.entities)
})

const quoteBlockClass = computed(() => {
  const textFollowsQuote = Boolean(messageText.value) && !event.value.item.files?.length
  return [
    'mx-2 mt-2 min-h-[54px]',
    textFollowsQuote ? '' : 'mb-2',
  ]
})

const hasForwarding = computed(() => Boolean(event.value.item.forwarding?.forwardingEventId))

const hasOwnContent = computed(() => {
  if (isMessageDeleted.value) {
    return true
  }
  if (messageText.value) {
    return true
  }
  if (event.value.item.files?.length) {
    return true
  }
  if (
    props.quotedEvent?.item.messageText ||
    !!props.quotedEvent?.item.files?.length ||
    (props.quotedEvent?.item.forwarding && props.quotedEvent.item.forwarding.forwardingEventId)
  ) {
    return true
  }
  return false
})

const onMessageHtmlClick = (clickEvent: MouseEvent) => {
  if (!isBotControlChat.value) {
    return
  }

  const botLink = getBotControlLinkFromEvent(clickEvent)
  if (!botLink) {
    return
  }

  clickEvent.preventDefault()
  clickEvent.stopPropagation()

  if (botLink.action === 'copy') {
    navigator.clipboard.writeText(botLink.payload)
      .then(() => notifySuccess(t('mainAside.copiedToClipboard')))
      .catch((e: unknown) => {
        console.error(e)
        notifyError(t('common.unknownError'))
      })
    return
  }

  if (!room.value) {
    return
  }

  useRoom().send(botLink.payload, room.value).catch((e: unknown) => {
    console.error(e)
    notifyError(e instanceof Error ? e.message : t('common.unknownError'))
  })
}

const {
  isExpanded,
  textElement,
  isTextTruncated,
  initTextExpanding,
  toggleExpand,
} = useTextExpand(messageText, LINES_COUNT_TO_CLAMP_MESSAGE)

const isPinned = computed(() => {
  return pinEvents.value?.find(i => i.eventId === props.event.eventId) as PinnedMessage
})

const isSingleVCardFile = computed(() => {
  const files = props.event.item.files
  return files?.length === 1 && isVCardFile(files[0]!)
})

const isVoice = computed(() => !isMessageDeleted.value && isVoiceMessage(event.value.item))
const hasVoiceTranscript = computed(() => {
  const cookie = event.value.item.files?.[0]?.filecookie
  return Boolean(cookie && transcripts.value.get(cookie) && expandedTranscripts.value.has(cookie))
})
const voiceFooterClass = computed(() => {
  if (!isVoice.value) return 'flex flex-col items-stretch gap-y-2 px-2 pb-2'
  if (hasVoiceTranscript.value) return 'flex flex-col items-stretch gap-y-1 px-2 pb-1.5'
  return 'relative z-1 -mt-4 flex flex-col items-stretch gap-y-1 px-2 pb-1.5'
})

const onMouseEnter = () => {
  if (header.value) {
    headerWidth.value = Math.ceil(header.value.offsetWidth)
  }

  isHovered.value = true
}

const { isUnread } = useUnreadMessages(props.event)
</script>
